"use client"

import { useState, useEffect } from "react"
import { View, StyleSheet } from "react-native"
import { Text, ActivityIndicator, Surface, useTheme, Button } from "react-native-paper"
import { GridAtGlanceSection } from "./GridAtGlanceSection"
import { MapSection } from "./MapSection"
import { ProjectsSection } from "./ProjectsSection"
import projectService from "../../services/projectService" // Import as default
import { useAuth } from "../../contexts/AuthContext"
import { useTheme as useAppTheme } from "../../contexts/ThemeContext"
import type { Project } from "../../types/project"
import { Zap, Radio, Route, AlertCircle } from "lucide-react"
import { useNavigation } from "@react-navigation/native"
import { Header } from "../../components/Header"
import { calculateGridMetrics, type GridMetricsResult } from "../../utils/gridMetrics"

export function AllProjectsDashboard() {
  const { user, logout, apiEndpoint } = useAuth()
  const { toggleTheme, isDarkMode } = useAppTheme()
  const theme = useTheme()
  const [assets, setAssets] = useState<Project[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [projectsWithData, setProjectsWithData] = useState<any[]>([])
  const [gridMetrics, setGridMetrics] = useState<GridMetricsResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigation = useNavigation()

  const loadProjects = async () => {
    try {
      setLoading(true)
      setError(null)

      if (!apiEndpoint) {
        setError("API endpoint not configured")
        return
      }

      // Fetch both assets and ongoing projects from the API endpoint
      const [apiAssets, apiProjects] = await Promise.all([
        projectService.getAssets(apiEndpoint),
        projectService.getOngoingProjects(apiEndpoint),
      ])
      setAssets(apiAssets)
      setProjects(apiProjects)

      // Combine both for metrics calculation
      const allItems = [...apiAssets, ...apiProjects]

      // Fetch project data for metrics calculation (from both assets and projects)
      const projectDataPromises = allItems.map(async (project) => {
        try {
          // Fetching project data from URL

          // First, fetch the list of files for this project
          const response = await fetch(project.url)
          if (!response.ok) {
            console.error(`Failed to fetch project files: ${response.statusText}`)
            throw new Error(`Failed to fetch project files: ${response.statusText}`)
          }

          // Check if the response is JSON
          const contentType = response.headers.get("content-type")
          if (!contentType || !contentType.includes("application/json")) {
            console.warn("Project URL returned non-JSON response:", { projectName: project.name, contentType })
            return null
          }

          const directoryData = await response.json()

          // Find the Info.json file in the files list
          const infoFile = directoryData.files?.find((file: any) => file.name === "Info.json")

          if (!infoFile) {
            console.warn("No Info.json found for project:", project.name)
            return null
          }

          // Fetching Info.json from URL

          // Fetch the Info.json file content
          const infoResponse = await fetch(infoFile.url)

          if (!infoResponse.ok) {
            console.error(`Failed to fetch Info.json for ${project.name}: ${infoResponse.statusText}`)
            throw new Error(`Failed to fetch Info.json: ${infoResponse.statusText}`)
          }

          // Check if Info.json response is JSON
          const infoContentType = infoResponse.headers.get("content-type")
          if (!infoContentType || !infoContentType.includes("application/json")) {
            console.warn("Info.json returned non-JSON response:", { projectName: project.name, infoContentType })
            return null
          }

          const infoData = await infoResponse.json()

          return {
            ...project,
            data: infoData,
          }
        } catch (err) {
          console.error(`Error fetching data for project ${project.name}:`, err)
          return null
        }
      })

      const results = await Promise.all(projectDataPromises)
      const validProjects = results.filter(Boolean)
      setProjectsWithData(validProjects)

      // Calculate grid metrics
      const metrics = calculateGridMetrics(validProjects)
      setGridMetrics(metrics)
    } catch (err: any) {
      console.error("Error loading projects:", err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (apiEndpoint) {
      loadProjects()
    }
  }, [apiEndpoint])

  const handleViewProject = (project: Project) => {
    // Pass both the project ID and the project name to the navigation
    navigation.navigate(
      "ProjectViewNested" as never,
      {
        projectId: project.id,
        projectName: project.name,
      } as never,
    )
  }

  // Prepare stats data based on calculated metrics
  const stats = [
    {
      icon: Zap,
      label: "Total Power",
      value: gridMetrics ? `${(gridMetrics.totalPower / 1000).toFixed(1)} GW` : "0 GW",
    },
    {
      icon: Radio,
      label: "Grid Lines",
      value: gridMetrics ? gridMetrics.totalLines.toString() : "0",
    },
    {
      icon: Route,
      label: "Total Distance",
      value: gridMetrics ? `${Math.round(gridMetrics.totalDistance)} km` : "0 km",
    },
    {
      icon: AlertCircle,
      label: "Alerts",
      value: gridMetrics ? gridMetrics.alerts.toString() : "0",
    },
  ]

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" />
      </View>
    )
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Error loading projects: {error}</Text>
        <Button mode="contained" onPress={loadProjects} style={{ marginTop: 16 }}>
          Retry
        </Button>
      </View>
    )
  }

  return (
    <View style={[styles.root, { backgroundColor: theme.colors.background }]}>
      {/* Header with logo */}
      <Header />

      {/* Main content */}
      <View style={styles.container}>
        <Text variant="titleLarge" style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
          Grid Info
        </Text>

        {/* Stats Cards */}
        <View style={styles.statsContainer}>
          {stats.map((stat, index) => (
            <Surface
              key={index}
              style={[styles.statCard, { backgroundColor: theme.colors.elevation.level1 }]}
              elevation={1}
            >
              <stat.icon size={20} color={theme.colors.primary} />
              <Text style={[styles.statValue, { color: theme.colors.onSurface }]}>{stat.value}</Text>
              <Text style={[styles.statLabel, { color: theme.colors.onSurfaceVariant }]}>{stat.label}</Text>
            </Surface>
          ))}
        </View>

        {/* Middle Section */}
        <View style={styles.middleSection}>
          <View style={styles.mapSection}>
            <Text variant="titleLarge" style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
              Grid
            </Text>
            <Surface style={[styles.mapContainer, { backgroundColor: theme.colors.elevation.level1 }]} elevation={1}>
              <MapSection projects={[...assets, ...projects]} />
            </Surface>
          </View>
          <View style={styles.gridSection}>
            <Text variant="titleLarge" style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
              Grid Overview
            </Text>
            <Surface style={[styles.gridContainer, { backgroundColor: theme.colors.elevation.level1 }]} elevation={1}>
              <GridAtGlanceSection metrics={{ projects: projectsWithData }} />
            </Surface>
          </View>
        </View>

        {/* Projects Section (Ongoing) */}
        <View style={{ marginTop: 24 }}>
          <ProjectsSection
            projects={projects}
            onView={handleViewProject}
            title="Projects (Ongoing/Construction)"
            emptyMessage="No ongoing projects"
          />
        </View>

        {/* Assets Section (Completed) */}
        <View style={{ marginTop: 24 }}>
          <ProjectsSection
            projects={assets}
            onView={handleViewProject}
            title="Assets (Completed)"
            emptyMessage="No completed assets"
          />
        </View>
      </View>
    </View>
  )
}

// Styles remain the same
const styles = StyleSheet.create({
  root: {
    flex: 1,
    height: "100vh",
    overflow: "hidden",
  },
  header: {
    height: 48,
    justifyContent: "center",
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    height: "100%",
    gap: 12,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
  },
  title: {
    fontWeight: "500",
  },
  container: {
    flex: 1,
    padding: 16,
    overflow: "auto",
    height: "calc(100vh - 48px)",
  },
  sectionTitle: {
    marginBottom: 12,
    fontWeight: "500",
  },
  statsContainer: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 16,
  },
  statCard: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    backgroundColor: "white",
    alignItems: "center",
    gap: 4,
  },
  statValue: {
    fontSize: 20,
    fontWeight: "600",
    marginTop: 2,
  },
  statLabel: {
    fontSize: 12,
    color: "#666",
  },
  middleSection: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 16,
    height: 350,
  },
  mapSection: {
    flex: 0.65,
    height: "100%",
  },
  mapContainer: {
    borderRadius: 12,
    overflow: "hidden",
    height: "100%",
  },
  gridSection: {
    flex: 0.35,
    height: "100%",
  },
  gridContainer: {
    borderRadius: 12,
    padding: 12,
    height: "100%",
    overflow: "auto",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  errorText: {
    color: "red",
    textAlign: "center",
  },
  emptyCard: {
    padding: 32,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  logo: {
    height: 30,
    width: 120,
    marginRight: 16,
  },
})

